import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageSEO from './PageSEO';
import Header from './Header';
import Footer from './Footer';
import { ChevronRight, Home, Brain, Crown, Sparkles } from 'lucide-react';

const SITE_URL = 'https://doktorcebinde.com';

export default function ClusterHubPage({ cluster }) {
  return (
    <div className="min-h-screen" style={{ background: 'transparent' }}>
      <PageSEO title={cluster.title} description={cluster.metaDescription} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'CollectionPage',
          'name': cluster.heading, 'description': cluster.metaDescription,
          'url': `${SITE_URL}/${cluster.slug}`,
          'mainEntity': { '@type': 'ItemList', 'itemListElement': cluster.tests.map((t, i) => ({
            '@type': 'ListItem', 'position': i + 1, 'name': t.name, 'url': `${SITE_URL}/${t.slug}`,
          })) },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Ana Sayfa', 'item': SITE_URL },
            { '@type': 'ListItem', 'position': 2, 'name': cluster.heading, 'item': `${SITE_URL}/${cluster.slug}` },
          ],
        })}</script>
      </Helmet>
      <Header />

      {/* Breadcrumb */}
      <nav className="max-w-3xl mx-auto px-4 pt-6 pb-2" aria-label="breadcrumb" data-testid="hub-breadcrumb">
        <ol className="flex items-center gap-1.5 text-xs">
          <li className="flex items-center gap-1">
            <Home className="w-3 h-3" style={{ color: '#64748B' }} />
            <Link to="/" style={{ color: '#64748B' }} className="hover:text-cyan-400 transition-colors">Ana Sayfa</Link>
          </li>
          <li style={{ color: '#475569' }}>/</li>
          <li><span style={{ color: '#E2E8F0' }} className="font-medium">{cluster.heading}</span></li>
        </ol>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-8 pb-20">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{
            background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)',
          }}>
            <Brain className="w-7 h-7" style={{ color: '#67E8F9' }} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="hub-title">{cluster.heading}</h1>
          <p className="text-base leading-relaxed max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {cluster.description}
          </p>
        </div>

        {/* Test Cards */}
        <div className="space-y-3 mb-12" data-testid="hub-test-list">
          {cluster.tests.map((t, i) => (
            <Link key={i} to={`/${t.slug}`}
              className="group flex items-center gap-4 px-5 py-5 rounded-2xl transition-all hover:-translate-y-0.5"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
              }}
              data-testid={`hub-test-${t.slug}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all" style={{
                background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.1)',
              }}>
                <span className="text-sm font-bold" style={{ color: '#67E8F9' }}>{i + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-bold text-white mb-0.5">{t.name}</h2>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{t.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1" style={{ color: '#475569' }} />
            </Link>
          ))}
        </div>

        {/* Premium CTA */}
        <div className="rounded-2xl p-6 text-center" style={{
          background: 'linear-gradient(170deg, rgba(250,204,21,0.04), rgba(245,158,11,0.02))',
          border: '1px solid rgba(250,204,21,0.1)',
        }} data-testid="hub-premium-cta">
          <Crown className="w-6 h-6 mx-auto mb-3" style={{ color: '#FACC15' }} />
          <p className="text-sm font-medium text-white mb-1">Tüm testlerin detaylı AI analizi Premium'da</p>
          <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.35)' }}>Kişisel risk değerlendirmesi ve takip planı</p>
          <Link to="/premium"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-black transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', boxShadow: '0 4px 15px rgba(250,204,21,0.25)' }}
            data-testid="hub-premium-btn">
            <Sparkles className="w-4 h-4" /> Premium'u Keşfet
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
